// src/hooks/useProfileImage.ts
import { useState, useCallback, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import api from '@/services/api';
import { useUser } from '@/context/UserContext'; // agora usa o contexto global
import axios from 'axios';
import { useTranslation } from 'react-i18next';

export const useProfileImage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { user, updateUser } = useUser();
  const { t } = useTranslation();

  const [imagePreview, setImagePreview] = useState<string | null>(
    user?.image?.url ?? null
  );
  const [isUploading, setIsUploading] = useState(false);
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // 🔄 Mantém o preview sincronizado com o user do contexto
  useEffect(() => {
    setImagePreview(user?.image?.url ?? null);
  }, [user?.image?.url]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setConfirmDialogOpen(true);
      setFileToUpload(file);
    }
  };

  const handleConfirmUpload = useCallback(async () => {
    if (!fileToUpload || !user) return;

    setConfirmDialogOpen(false);
    setIsUploading(true);

    const formData = new FormData();
    formData.append('file', fileToUpload);
    formData.append('fileTypeId', '1');

    try {
      const response = await api.put(
        `/users/${user.id}/upload-image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.status === 200) {
        // 🔹 Atualiza o contexto global (reactiva todos os componentes que usam `user`)
        updateUser?.(response.data);

        // 🔹 Atualiza preview local se quiser mostrar no modal antes de fechar
        setImagePreview(response.data.image.url);

        enqueueSnackbar(t('config.personalData.updateImageSuccess'), {
          variant: 'success',
        });
      }

      console.log('User antigo:', user);
      console.log('User novo:', response.data);
      console.log('É igual?', user === response.data);
    } catch (error) {
      console.error('Erro no upload:', error);
      enqueueSnackbar(t('config.personalData.updateImageFailed'), {
        variant: 'error',
      });
    } finally {
      setIsUploading(false);
      setFileToUpload(null);
    }
  }, [fileToUpload, user, updateUser, enqueueSnackbar]);

  const handleRemoveImage = async () => {
    if (!user) return; // Garante que o usuário existe

    try {
      // 💡 CORREÇÃO: Usar 'user.id' diretamente do contexto
      await api.delete(`/users/${user.id}/remove-image`);

      // Se o backend retornou sucesso, atualiza o contexto para remover a imagem.
      const updatedUser = { ...user, image: null };

      if (updateUser) {
        updateUser(updatedUser);
      } else {
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }

      enqueueSnackbar(t('config.personalData.removeImageSuccess'), {
        variant: 'success',
      });

      setImagePreview(null);
    } catch (error: unknown) {
      console.log('Erro capturado!', error);
      if (axios.isAxiosError(error) && error.response) {
        enqueueSnackbar(
          error.response.data?.detail ||
            t('config.personalData.removeImageFailed'),
          {
            variant: 'error',
          }
        );
      }
    }
  };

  // 🔄 Limpa o object URL pra evitar memory leaks
  useEffect(() => {
    let objectUrl: string | null = null;
    if (fileToUpload) {
      objectUrl = URL.createObjectURL(fileToUpload);
      setImagePreview(objectUrl);
    }
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [fileToUpload]);

  return {
    user, // opcional, caso queira acessar direto
    imagePreview,
    isUploading,
    confirmDialogOpen,
    setImagePreview,
    setConfirmDialogOpen,
    handleFileSelect,
    handleConfirmUpload,
    handleRemoveImage,
  };
};
